import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { tendersAPI } from '../api/api';
import { toast } from '../hooks/use-toast';
import AppLayout from '../components/AppLayout';
import {
  ArrowLeft, Briefcase, Building2, MapPin, Clock, Calendar, AlertCircle,
  Sparkles, Check, X, Star, ChevronDown, ChevronRight, Loader2, RefreshCw,
  Copy, Trash2, Edit3, Save, FileText, Download,
} from 'lucide-react';

// ───────────────────────────────────────────────────────────────────────────
// Status definitions — full workflow
// ───────────────────────────────────────────────────────────────────────────
const STATUSES = {
  brouillon: { label: 'Brouillon', color: '#94a3b8', bg: 'rgba(148,163,184,0.12)', border: 'rgba(148,163,184,0.35)' },
  actif:     { label: 'Actif',     color: '#7B68EE', bg: 'rgba(123,104,238,0.15)', border: 'rgba(123,104,238,0.4)' },
  soumis:    { label: 'Soumis',    color: '#60A5FA', bg: 'rgba(96,165,250,0.14)',  border: 'rgba(96,165,250,0.4)'  },
  gagne:     { label: 'Gagné',     color: '#4ADE80', bg: 'rgba(74,222,128,0.14)',  border: 'rgba(74,222,128,0.4)'  },
  perdu:     { label: 'Perdu',     color: '#F87171', bg: 'rgba(248,113,113,0.14)', border: 'rgba(248,113,113,0.4)' },
  archive:   { label: 'Archivé',   color: '#6b7280', bg: 'rgba(107,114,128,0.10)', border: 'rgba(107,114,128,0.30)'},
};

const formatDeadline = (deadline) => {
  if (!deadline) return null;
  const now = new Date();
  const d = new Date(deadline);
  const diff = Math.floor((d - now) / 86400000);
  if (diff < 0) return { label: `Dépassée il y a ${Math.abs(diff)}j`, color: '#F87171', urgent: true };
  if (diff === 0) return { label: "Aujourd'hui", color: '#FB923C', urgent: true };
  if (diff <= 3) return { label: `Dans ${diff}j`, color: '#FB923C', urgent: true };
  if (diff <= 7) return { label: `Dans ${diff}j`, color: '#FACC15', urgent: false };
  return { label: `Dans ${diff}j`, color: 'var(--muted2)', urgent: false };
};

const scoreColor = (score) => {
  if (score >= 75) return { color: '#4ADE80', bg: 'rgba(74,222,128,0.12)' };
  if (score >= 50) return { color: '#FACC15', bg: 'rgba(250,204,21,0.12)' };
  if (score >= 25) return { color: '#FB923C', bg: 'rgba(251,146,60,0.12)' };
  return { color: '#F87171', bg: 'rgba(248,113,113,0.10)' };
};

// Pastille de score. L'anneau précédent devenait illisible sous ~15 % (l'arc
// disparaissait et il ne restait qu'un chiffre) : ici le chiffre porte la
// valeur, la barre en donne la lecture visuelle à toutes les valeurs, et
// l'origine du score (IA ou mots-clés) est écrite en toutes lettres.
const ScoreBadge = ({ score, source }) => {
  const c = scoreColor(score);
  const pct = Math.max(0, Math.min(100, Math.round(score)));
  return (
    <div style={{ width: 58, flexShrink: 0, textAlign: 'center' }}>
      <div style={{
        background: c.bg, border: `1px solid ${c.color}40`,
        borderRadius: 9, padding: '7px 0 6px',
      }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: c.color, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
          {pct}
        </div>
        <div style={{ height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.09)', margin: '6px 8px 0' }}>
          <div style={{
            width: `${pct}%`, height: '100%', borderRadius: 2, background: c.color,
            transition: 'width 0.4s ease-out',
          }} />
        </div>
      </div>
      <div style={{ fontSize: 9.5, fontWeight: 600, color: 'var(--muted2)', marginTop: 4, letterSpacing: 0.2 }}>
        {source}
      </div>
    </div>
  );
};

// ───────────────────────────────────────────────────────────────────────────
// Main detail page
// ───────────────────────────────────────────────────────────────────────────
const TenderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [tender, setTender] = useState(null);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null); // dossier_id currently expanded
  const [explainingFor, setExplainingFor] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showShortlistOnly, setShowShortlistOnly] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [t, m] = await Promise.all([tendersAPI.getOne(id), tendersAPI.matches(id)]);
      setTender(t);
      setNotesDraft(t.notes || '');
      setMatches(m);
    } catch (err) {
      toast({ title: 'Erreur', description: 'Impossible de charger l\'AO.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const changeStatus = async (newStatus) => {
    if (updatingStatus || !tender || newStatus === tender.status) return;
    setUpdatingStatus(true);
    try {
      const updated = await tendersAPI.update(id, { status: newStatus });
      setTender(updated);
      toast({ title: 'Statut mis à jour', description: `Cet AO est maintenant « ${STATUSES[newStatus]?.label} »` });
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleExplain = async (dossierId) => {
    if (explainingFor) return;
    setExplainingFor(dossierId);
    try {
      const explanation = await tendersAPI.explain(id, dossierId);
      setMatches(prev => prev.map(m =>
        m.dossier_id === dossierId
          ? { ...m, ai_score: explanation.score, ai_explanation: explanation, ai_computed_at: new Date().toISOString() }
          : m
      ));
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setExplainingFor(null);
    }
  };

  const toggleShortlist = async (m) => {
    try {
      if (m.shortlisted) {
        await tendersAPI.shortlistRemove(id, m.dossier_id);
      } else {
        await tendersAPI.shortlistAdd(id, m.dossier_id, null);
      }
      setMatches(prev => prev.map(x =>
        x.dossier_id === m.dossier_id ? { ...x, shortlisted: !x.shortlisted } : x
      ));
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const recompute = async () => {
    try {
      await tendersAPI.recompute(id);
      const m = await tendersAPI.matches(id);
      setMatches(m);
      toast({ title: 'Scores recalculés', description: `${m.length} consultants évalués.` });
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const handleDuplicate = async () => {
    try {
      const newT = await tendersAPI.duplicate(id);
      toast({ title: 'AO dupliqué', description: 'Une copie a été créée en brouillon.' });
      navigate(`/tenders/${newT.id}`);
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cet AO ?")) return;
    try {
      await tendersAPI.delete(id);
      navigate('/tenders');
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const handleExportShortlist = async () => {
    if (exporting) return;
    if (shortlistCount === 0) {
      toast({ title: 'Shortlist vide', description: 'Ajoutez au moins un consultant avant d\'exporter.', variant: 'destructive' });
      return;
    }
    setExporting(true);
    try {
      await tendersAPI.exportShortlistPDF(id, tender?.title);
      toast({ title: 'Shortlist exportée', description: 'Le PDF combiné a été téléchargé.' });
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || err.message || 'Export impossible.', variant: 'destructive' });
    } finally {
      setExporting(false);
    }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      const updated = await tendersAPI.update(id, { notes: notesDraft });
      setTender(updated);
      setEditingNotes(false);
      toast({ title: 'Notes enregistrées' });
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setSavingNotes(false);
    }
  };

  if (loading || !tender) {
    return (
      <AppLayout title="Appel d'offre">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, gap: 10, color: 'var(--muted2)' }}>
          <Loader2 size={20} style={{ animation: 'sd-spin 1s linear infinite' }} />
          Chargement…
        </div>
        <style>{`@keyframes sd-spin { to { transform: rotate(360deg); } }`}</style>
      </AppLayout>
    );
  }

  const status = STATUSES[tender.status] || STATUSES.brouillon;
  const deadlineInfo = formatDeadline(tender.deadline);
  const shownMatches = showShortlistOnly ? matches.filter(m => m.shortlisted) : matches;
  const shortlistCount = matches.filter(m => m.shortlisted).length;

  return (
    <AppLayout title={tender.title || "Appel d'offre"}>
      <div style={{ marginBottom: 20 }}>
        <button onClick={() => navigate('/tenders')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            background: 'none', border: 'none', color: 'var(--muted2)',
            fontSize: 12, fontWeight: 500, cursor: 'pointer', padding: 0, fontFamily: 'inherit',
            marginBottom: 8,
          }}>
          <ArrowLeft size={13} /> Tous les AO
        </button>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 18, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 280 }}>
            <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--white)', letterSpacing: -0.4, marginBottom: 6 }}>
              {tender.title}
            </h1>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, fontSize: 12, color: 'var(--muted2)' }}>
              {tender.client && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Building2 size={12} /> {tender.client}</span>}
              {tender.sector && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Briefcase size={12} /> {tender.sector}</span>}
              {tender.location && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><MapPin size={12} /> {tender.location}</span>}
              {tender.duration && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Clock size={12} /> {tender.duration}</span>}
              {tender.required_experience_years && (
                <span>{tender.required_experience_years}+ ans d'exp.</span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handleDuplicate} className="sd-btn" style={{ border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--muted2)' }}>
              <Copy size={12} /> Dupliquer
            </button>
            <button onClick={handleDelete} className="sd-btn" style={{ border: '1px solid rgba(239,68,68,0.3)', background: 'transparent', color: '#F87171' }}>
              <Trash2 size={12} />
            </button>
          </div>
        </div>
      </div>

      {/* Statut + deadline — barre d'outils, pas une carte : six pastilles ne
          justifient pas un conteneur aussi lourd que le contenu de la mission.
          Le statut courant se lit par un contrôle segmenté continu. */}
      <div style={{
        padding: '0 0 16px', marginBottom: 16,
        borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
      }}>
        <div style={{
          display: 'inline-flex', gap: 2, flexWrap: 'wrap',
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 10, padding: 3,
        }}>
          {Object.entries(STATUSES).map(([k, s]) => (
            <button key={k}
              onClick={() => changeStatus(k)}
              disabled={updatingStatus}
              aria-pressed={tender.status === k}
              style={{
                fontSize: 11.5, fontWeight: 600,
                padding: '5px 12px', borderRadius: 8,
                background: tender.status === k ? s.bg : 'transparent',
                color: tender.status === k ? s.color : 'var(--muted2)',
                border: `1px solid ${tender.status === k ? s.border : 'transparent'}`,
                cursor: updatingStatus ? 'wait' : 'pointer',
                fontFamily: 'inherit', transition: 'all 0.15s',
              }}>
              {s.label}
            </button>
          ))}
        </div>
        {deadlineInfo && (
          <div style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            <span style={{ color: 'var(--muted)' }}>Deadline :</span>
            <span style={{
              color: deadlineInfo.color, fontWeight: 600,
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '3px 9px',
              background: deadlineInfo.urgent ? 'rgba(248,113,113,0.08)' : 'transparent',
              borderRadius: 10,
              border: `1px solid ${deadlineInfo.urgent ? 'rgba(248,113,113,0.25)' : 'var(--border)'}`,
            }}>
              {deadlineInfo.urgent ? <AlertCircle size={11} /> : <Calendar size={11} />}
              {deadlineInfo.label}
            </span>
          </div>
        )}
      </div>

      {/* Mission summary + skills + notes — 2-col layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 14, marginBottom: 20 }}>

        {/* Résumé et compétences décrivent la même chose — la mission. Deux
            cartes distinctes leur donnaient un poids égal et aplatissaient la
            hiérarchie de la page ; un seul bloc, séparé d'un filet. */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, minWidth: 0 }}>
          {tender.mission_summary && (
            <div style={{ padding: '16px 18px' }}>
              <div style={{ fontSize: 13.5, color: 'var(--white)', lineHeight: 1.65 }}>{tender.mission_summary}</div>
            </div>
          )}

          <div style={{
            padding: '14px 18px 16px',
            borderTop: tender.mission_summary ? '1px solid var(--border)' : 'none',
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: 'var(--muted2)', marginBottom: 10 }}>
              Compétences requises ({(tender.required_skills || []).length})
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {(tender.required_skills || []).map((s, i) => (
                <span key={i} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  fontSize: 11.5, fontWeight: 500,
                  padding: '4px 10px', borderRadius: 8,
                  background: s.importance === 'must_have' ? 'rgba(123,104,238,0.13)' : 'transparent',
                  color: s.importance === 'must_have' ? 'var(--vio2)' : 'var(--muted2)',
                  border: `1px solid ${s.importance === 'must_have' ? 'rgba(123,104,238,0.35)' : 'var(--border)'}`,
                }}
                title={s.importance === 'must_have' ? 'Compétence indispensable' : 'Compétence souhaitée'}>
                  {s.importance === 'must_have' && <span style={{ width: 4, height: 4, borderRadius: 2, background: 'currentColor' }} />}
                  {s.name}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Notes column */}
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--muted2)' }}>Notes internes</span>
            {!editingNotes ? (
              <button onClick={() => setEditingNotes(true)} style={{ background: 'none', border: 'none', color: 'var(--muted2)', cursor: 'pointer', padding: 2, fontFamily: 'inherit' }}>
                <Edit3 size={13} />
              </button>
            ) : (
              <div style={{ display: 'flex', gap: 4 }}>
                <button onClick={() => { setEditingNotes(false); setNotesDraft(tender.notes || ''); }} disabled={savingNotes}
                  style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', padding: 2, fontFamily: 'inherit' }}>
                  <X size={13} />
                </button>
                <button onClick={saveNotes} disabled={savingNotes}
                  style={{ background: 'none', border: 'none', color: 'var(--vio2)', cursor: 'pointer', padding: 2, fontFamily: 'inherit' }}>
                  {savingNotes ? <Loader2 size={13} className="sd-spin" /> : <Save size={13} />}
                </button>
              </div>
            )}
          </div>
          {editingNotes ? (
            <textarea
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              rows={5}
              placeholder="Notes internes sur cet AO (contacts, brief client, points à creuser…)"
              style={{
                width: '100%', resize: 'vertical',
                background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 6,
                padding: '8px 10px', color: 'var(--white)', fontSize: 12,
                outline: 'none', fontFamily: 'inherit',
              }}
            />
          ) : (
            <div style={{ fontSize: 12, color: tender.notes ? 'var(--white)' : 'var(--muted)', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
              {tender.notes || <em>Aucune note pour le moment.</em>}
            </div>
          )}
        </div>
      </div>

      {/* ═════════ Matches table ═════════ */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--white)' }}>
            Correspondances · {matches.length} consultant{matches.length !== 1 ? 's' : ''}
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--muted2)', marginTop: 2 }}>
            Classés par score. Ouvrez une ligne pour le détail des compétences.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={() => setShowShortlistOnly(v => !v)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '7px 12px', borderRadius: 7,
              border: '1px solid', borderColor: showShortlistOnly ? '#FACC15' : 'var(--border)',
              background: showShortlistOnly ? 'rgba(250,204,21,0.10)' : 'var(--surface)',
              color: showShortlistOnly ? '#FACC15' : 'var(--muted2)',
              fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
            }}>
            <Star size={13} fill={showShortlistOnly ? '#FACC15' : 'none'} />
            Shortlist ({shortlistCount})
          </button>
          {shortlistCount > 0 && (
            <button onClick={handleExportShortlist} disabled={exporting}
              title="Exporter en PDF combiné (cover sheet AO + DCs shortlistés)"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '7px 12px', borderRadius: 7,
                border: '1px solid rgba(123,104,238,0.4)',
                background: 'rgba(123,104,238,0.10)',
                color: 'var(--vio2)',
                fontSize: 12, fontWeight: 600,
                cursor: exporting ? 'wait' : 'pointer', opacity: exporting ? 0.7 : 1,
                fontFamily: 'inherit',
              }}>
              {exporting ? <Loader2 size={12} className="sd-spin" /> : <Download size={12} />}
              Export PDF
            </button>
          )}
          <button onClick={recompute}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '7px 12px', borderRadius: 7,
              border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--muted2)',
              fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
            }}>
            <RefreshCw size={12} /> Recalculer
          </button>
        </div>
      </div>

      {shownMatches.length === 0 ? (
        <div style={{
          background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12,
          padding: 40, textAlign: 'center',
        }}>
          <FileText size={32} color="var(--muted)" style={{ marginBottom: 10 }} />
          <div style={{ fontSize: 13, color: 'var(--muted2)' }}>
            {showShortlistOnly
              ? 'Aucun consultant dans la shortlist.'
              : 'Aucun consultant pour le moment. Ajoutez des dossiers de compétences pour voir des correspondances.'}
          </div>
        </div>
      ) : (
        // Une seule surface, lignes séparées par un filet : quatre cartes
        // autonomes pour quatre lignes de liste produisaient plus de cadres
        // que d'information.
        <div style={{
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 12, overflow: 'hidden',
        }}>
          {shownMatches.map((m, i) => (
            <MatchRow
              key={m.dossier_id}
              match={m}
              isFirst={i === 0}
              expanded={expanded === m.dossier_id}
              onToggle={() => setExpanded(expanded === m.dossier_id ? null : m.dossier_id)}
              onExplain={() => handleExplain(m.dossier_id)}
              explaining={explainingFor === m.dossier_id}
              onToggleShortlist={() => toggleShortlist(m)}
              onOpenDossier={() => navigate(`/dossiers/${m.dossier_id}/edit`)}
            />
          ))}
        </div>
      )}

      <style>{`@keyframes sd-spin { to { transform: rotate(360deg); } } .sd-spin { animation: sd-spin 0.9s linear infinite; }`}</style>
    </AppLayout>
  );
};

// ───────────────────────────────────────────────────────────────────────────
// MatchRow — collapsed summary + expanded breakdown + AI explanation
// ───────────────────────────────────────────────────────────────────────────
const MatchRow = ({ match, isFirst, expanded, onToggle, onExplain, explaining, onToggleShortlist, onOpenDossier }) => {
  const ai = match.ai_explanation;
  const displayScore = match.ai_score != null ? match.ai_score : match.score_keyword;
  const bd = match.score_breakdown || {};
  const matched = bd.matched || [];
  const missingMust = bd.missing_must || [];
  const missingNice = bd.missing_nice || [];

  return (
    <div style={{
      borderTop: isFirst ? 'none' : '1px solid var(--border)',
      background: expanded ? 'rgba(255,255,255,0.02)' : 'transparent',
      transition: 'background 0.15s',
    }}>
      {/* Collapsed row */}
      <div
        onClick={onToggle}
        style={{
          display: 'flex', alignItems: 'center', gap: 14,
          padding: '12px 16px',
          cursor: 'pointer',
        }}
      >
        <ScoreBadge score={displayScore} source={match.ai_score != null ? 'IA' : 'mots-clés'} />

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--white)', marginBottom: 2 }}>
            {match.consultant_name || 'Consultant sans nom'}
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--muted2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {match.consultant_title || 'Poste non renseigné'}
          </div>
        </div>

        {/* Couverture des compétences — le libellé porte le sens, la couleur
            ne fait que le renforcer (lisible en daltonisme comme en N&B). */}
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--white)', fontVariantNumeric: 'tabular-nums' }}>
            {matched.length}<span style={{ color: 'var(--muted2)', fontWeight: 500 }}>/{matched.length + missingMust.length + missingNice.length}</span>
          </div>
          <div style={{ fontSize: 10.5, color: missingMust.length > 0 ? '#F87171' : 'var(--muted2)', marginTop: 1 }}>
            {missingMust.length > 0 ? `${missingMust.length} indispensable${missingMust.length > 1 ? 's' : ''} manque${missingMust.length > 1 ? 'nt' : ''}` : 'compétences'}
          </div>
        </div>

        <button
          onClick={(e) => { e.stopPropagation(); onToggleShortlist(); }}
          title={match.shortlisted ? 'Retirer de la shortlist' : 'Ajouter à la shortlist'}
          style={{
            background: match.shortlisted ? 'rgba(250,204,21,0.12)' : 'transparent',
            border: '1px solid', borderColor: match.shortlisted ? 'rgba(250,204,21,0.4)' : 'var(--border)',
            borderRadius: 7, padding: 6, cursor: 'pointer',
            color: match.shortlisted ? '#FACC15' : 'var(--muted2)',
          }}
        >
          <Star size={13} fill={match.shortlisted ? '#FACC15' : 'none'} />
        </button>

        {expanded ? <ChevronDown size={16} color="var(--muted2)" /> : <ChevronRight size={16} color="var(--muted2)" />}
      </div>

      {/* Expanded section */}
      {expanded && (
        <div style={{ padding: '4px 16px 16px', borderTop: '1px solid var(--border)' }}>
          {/* Skills breakdown */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginTop: 12 }}>
            <SkillsList title="Compétences trouvées" color="#4ADE80" items={matched.map(s => s.name)} icon={<Check size={11} />} />
            {missingMust.length > 0 && (
              <SkillsList title="Manquantes (obligatoires)" color="#F87171" items={missingMust.map(s => s.name)} icon={<X size={11} />} />
            )}
            {missingNice.length > 0 && (
              <SkillsList title="Manquantes (souhaitables)" color="#FACC15" items={missingNice.map(s => s.name)} icon={<X size={11} />} />
            )}
          </div>

          {/* AI explanation block */}
          <div style={{
            marginTop: 14, padding: 12,
            background: 'rgba(123,104,238,0.05)',
            border: '1px solid rgba(123,104,238,0.2)',
            borderRadius: 8,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: ai ? 10 : 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--vio2)', fontSize: 11.5, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase' }}>
                <Sparkles size={13} />
                Analyse IA
              </div>
              {!ai && (
                <button onClick={onExplain} disabled={explaining}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '7px 13px', borderRadius: 7,
                    background: 'var(--vio)', border: 'none', color: '#fff',
                    fontSize: 12, fontWeight: 600,
                    cursor: explaining ? 'wait' : 'pointer', opacity: explaining ? 0.7 : 1,
                    fontFamily: 'inherit',
                  }}>
                  {explaining ? <Loader2 size={12} className="sd-spin" /> : <Sparkles size={12} />}
                  {explaining ? 'Analyse en cours…' : 'Pourquoi ce match ?'}
                </button>
              )}
            </div>
            {ai && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {ai.verdict && (
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--white)' }}>
                    {ai.verdict}
                  </div>
                )}
                {ai.strengths?.length > 0 && (
                  <div>
                    <div style={{ fontSize: 10.5, fontWeight: 700, color: '#4ADE80', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 4 }}>Points forts</div>
                    <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: 'var(--muted2)', lineHeight: 1.55 }}>
                      {ai.strengths.map((s, i) => <li key={i} style={{ marginBottom: 3 }}>{s}</li>)}
                    </ul>
                  </div>
                )}
                {ai.gaps?.length > 0 && (
                  <div>
                    <div style={{ fontSize: 10.5, fontWeight: 700, color: '#F87171', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 4 }}>Lacunes</div>
                    <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: 'var(--muted2)', lineHeight: 1.55 }}>
                      {ai.gaps.map((g, i) => <li key={i} style={{ marginBottom: 3 }}>{g}</li>)}
                    </ul>
                  </div>
                )}
                {ai.recommendation && (
                  <div style={{
                    padding: '8px 12px', borderRadius: 6,
                    background: 'rgba(123,104,238,0.10)',
                    border: '1px solid rgba(123,104,238,0.3)',
                    fontSize: 12, color: 'var(--white)', fontWeight: 500,
                  }}>
                    <strong style={{ color: 'var(--vio2)' }}>Recommandation :</strong> {ai.recommendation}
                  </div>
                )}
              </div>
            )}
          </div>

          <div style={{ marginTop: 12, textAlign: 'right' }}>
            <button onClick={onOpenDossier}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '6px 12px', borderRadius: 6,
                background: 'transparent', border: '1px solid var(--border)', color: 'var(--muted2)',
                fontSize: 11.5, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
              }}>
              Ouvrir le DC →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const SkillsList = ({ title, color, items, icon }) => (
  <div>
    <div style={{ fontSize: 10.5, fontWeight: 700, color, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 6 }}>
      {title} ({items.length})
    </div>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {items.length === 0 ? (
        <em style={{ fontSize: 11, color: 'var(--muted)' }}>—</em>
      ) : items.map((name, i) => (
        <span key={i} style={{
          display: 'inline-flex', alignItems: 'center', gap: 3,
          fontSize: 10.5, fontWeight: 500,
          padding: '3px 8px', borderRadius: 10,
          background: `${color}1a`, color, border: `1px solid ${color}55`,
        }}>
          {icon}{name}
        </span>
      ))}
    </div>
  </div>
);

export default TenderDetail;
